import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { STORAGE_KEY } from '../constants';

// Endpoints that legitimately answer 401 without meaning "your session ended".
const PUBLIC_PATHS = ['/authenticate', '/access-requests', '/reset-password'];

function storedToken(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored) as { token?: string }).token || '' : '';
  } catch {
    return '';
  }
}

/** True only for the exact public endpoints (so `/admin/access-requests` is NOT public). */
function isPublic(config: InternalAxiosRequestConfig | undefined): boolean {
  if (!config) return false;
  try {
    // getUri joins baseURL + url exactly as axios will when it sends the request.
    return PUBLIC_PATHS.includes(new URL(axios.getUri(config), window.location.origin).pathname);
  } catch {
    return false;
  }
}

function endSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
  if (window.location.pathname !== '/login') window.location.assign('/login');
}

/**
 * Sends `Authorization: Bearer <token>` on every request and signs the user out when the
 * backend rejects the token (expired or invalid). Safe to call once per axios instance.
 */
export function installAuth(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    const token = storedToken();
    if (token && !isPublic(config)) config.headers.set('Authorization', `Bearer ${token}`);
    return config;
  });
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error) && error.response?.status === 401 && !isPublic(error.config)) {
        endSession();
      }
      return Promise.reject(error);
    },
  );
}

/** Downloads a file through axios so the token is attached (plain `<a href>` links cannot). */
export async function downloadWithAuth(url: string, filename: string): Promise<void> {
  const response = await axios.get<Blob>(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
